export interface PopoutWindow {
  id: string;
  window: Window;
  tabType: string;
  title: string;
}

export class WindowManager {
  private static instance: WindowManager;
  private windows: Map<string, PopoutWindow> = new Map();
  private messageHandlers: ((data: any) => void)[] = [];

  static getInstance(): WindowManager {
    if (!WindowManager.instance) {
      WindowManager.instance = new WindowManager();
    }
    return WindowManager.instance;
  }

  constructor() {
    // Listen for messages from pop-out windows
    window.addEventListener('message', (event) => {
      if (event.origin !== window.location.origin) return;
      
      this.messageHandlers.forEach(handler => handler(event.data));
    });

    // Clean up closed windows
    window.addEventListener('beforeunload', () => {
      this.closeAllWindows();
    });
  }

  openPopout(tabType: string, title: string): string {
    const windowId = `popout-${tabType}-${Date.now()}`;
    
    const popoutWindow = window.open(
      '', 
      windowId,
      'width=1200,height=800,scrollbars=yes,resizable=yes,status=yes,toolbar=no,menubar=no,location=no'
    );

    if (!popoutWindow) {
      throw new Error('Failed to open pop-out window. Please allow pop-ups for this site.');
    }

    // Set up the pop-out window
    this.setupPopoutWindow(popoutWindow, tabType, title, windowId);

    const popout: PopoutWindow = {
      id: windowId,
      window: popoutWindow,
      tabType,
      title
    };

    this.windows.set(windowId, popout);

    // Monitor window close
    const checkClosed = () => {
      if (popoutWindow.closed) {
        this.windows.delete(windowId);
      } else {
        setTimeout(checkClosed, 1000);
      }
    };
    checkClosed();

    return windowId;
  }

  private setupPopoutWindow(popoutWindow: Window, tabType: string, title: string, windowId: string) {
    const doc = popoutWindow.document;
    
    // Set up HTML with iframe to load the main app
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${title} - Engramiq Solar Farm</title>
          <style>
            body { 
              margin: 0; 
              padding: 0; 
              font-family: 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              background: #0d1830;
              color: #f4f4f4;
              overflow: hidden;
            }
            #popout-container { 
              width: 100%; 
              height: 100vh; 
              display: flex;
              flex-direction: column;
            }
            #popout-header {
              background: linear-gradient(135deg, #17c480, #0d1830);
              padding: 12px 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 1px solid rgba(23, 196, 128, 0.2);
            }
            #popout-content {
              flex: 1;
              background: #0d1830;
              overflow: hidden;
            }
            .close-btn {
              background: rgba(255, 255, 255, 0.1);
              border: none;
              color: #0d1830;
              font-size: 16px;
              font-weight: bold;
              width: 30px;
              height: 30px;
              border-radius: 6px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .close-btn:hover {
              background: rgba(255, 255, 255, 0.2);
            }
            h1 {
              margin: 0;
              font-size: 18px;
              font-weight: 600;
              color: #0d1830;
            }
          </style>
        </head>
        <body>
          <div id="popout-container">
            <div id="popout-header">
              <h1>${title}</h1>
              <button class="close-btn" onclick="closeWindow()">&times;</button>
            </div>
            <div id="popout-content">
              <div id="root"></div>
            </div>
          </div>
          <script>
            window.windowId = '${windowId}';
            window.tabType = '${tabType}';
            
            function closeWindow() {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({
                  type: 'close-popout',
                  windowId: '${windowId}'
                }, window.location.origin);
              }
              window.close();
            }
            
            // Communication with parent window
            function sendToParent(data) {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({
                  type: 'popout-message',
                  windowId: '${windowId}',
                  ...data
                }, window.location.origin);
              }
            }
            
            // Listen for messages from parent
            window.addEventListener('message', function(event) {
              if (event.origin !== window.location.origin) return;
              if (event.data.type === 'app-state-update') {
                window.updateAppState && window.updateAppState(event.data.state);
              }
            });
            
            // Notify parent when ready
            sendToParent({ type: 'window-ready' });
          </script>
        </body>
      </html>
    `);
    doc.close();
  }

  sendToWindow(windowId: string, data: any) {
    const popout = this.windows.get(windowId);
    if (popout && !popout.window.closed) {
      popout.window.postMessage({
        type: 'app-state-update',
        ...data
      }, window.location.origin);
    }
  }

  sendToAllWindows(data: any) {
    this.windows.forEach((popout) => {
      if (!popout.window.closed) {
        popout.window.postMessage({
          type: 'app-state-update',
          ...data
        }, window.location.origin);
      }
    });
  }

  closeWindow(windowId: string) {
    const popout = this.windows.get(windowId);
    if (popout) {
      popout.window.close();
      this.windows.delete(windowId);
    }
  }

  closeAllWindows() {
    this.windows.forEach((popout) => {
      popout.window.close();
    });
    this.windows.clear();
  }

  onMessage(handler: (data: any) => void) {
    this.messageHandlers.push(handler);
  }

  getOpenWindows(): PopoutWindow[] {
    return Array.from(this.windows.values()).filter(popout => !popout.window.closed);
  }
}

export const windowManager = WindowManager.getInstance();